/**
 * RiderPricePageTab · v2.6.9 batch BP
 * ====================================
 * Admin Console → Administration → Rider Price Page.
 *
 * Loads the current CMS doc from /api/admin/rider-prices/config (well —
 * /api/rider-prices/config since GET is public), renders an editable form
 * for every field on the rider-facing Prices page, and PUTs the entire doc
 * back on save. The rider page reads the same endpoint so changes take
 * effect on the rider's next page-load (no deploy).
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Save, RotateCcw, Plus, Trash2, ArrowUp, ArrowDown, Receipt, AlertTriangle, ExternalLink } from "lucide-react";
import adminApi from "@/lib/adminApi";
import api from "@/lib/api";
import { toast } from "sonner";

const COLOR_OPTIONS = [
  { value: "emerald", label: "Emerald (green)" },
  { value: "amber",   label: "Amber (yellow)" },
  { value: "indigo",  label: "Indigo (blue)" },
  { value: "rose",    label: "Rose (pink)" },
];

export default function RiderPricePageTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // v2.7.12 · Ensure every panel + rate has a stable client-side id so React
  // keys survive reorder/insert. The backend doesn't care about these extra
  // fields (it ignores unknown keys in the panels schema).
  const _normalize = (doc) => {
    if (!doc?.panels) return doc;
    doc.panels = doc.panels.map((p, pi) => ({
      ...p,
      id: p.id || `panel-load-${Date.now()}-${pi}`,
      rates: (p.rates || []).map((r, ri) => ({
        ...r,
        _rid: r._rid || `rate-load-${Date.now()}-${pi}-${ri}`,
      })),
    }));
    return doc;
  };

  const load = () => {
    setLoading(true);
    api.get("/rider-prices/config")
      .then((r) => { setCfg(_normalize(r.data)); setDirty(false); })
      .catch(() => toast.error("Could not load price page config"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const update = (mutator) => {
    setCfg((c) => {
      const next = JSON.parse(JSON.stringify(c));
      mutator(next);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/rider-prices/config", cfg);
      toast.success("Rider Prices page updated — riders will see changes on next load");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset the rider Prices page to factory defaults? This cannot be undone.")) return;
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/rider-prices/reset");
      setCfg(_normalize(data.config)); setDirty(false);
      toast.success("Reset to factory defaults");
    } catch {
      toast.error("Reset failed");
    } finally { setSaving(false); }
  };

  if (loading || !cfg) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500" data-testid="rpp-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto"/>
        <div className="mt-2 text-xs uppercase tracking-widest">Loading config…</div>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="rider-price-page-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-extrabold flex items-center gap-2"><Receipt className="h-5 w-5 text-amber-400"/>Rider Price Page</h2>
          <p className="text-xs text-zinc-400">Edit every word + rate on the rider-facing Prices page. Changes go live immediately for new page-loads.</p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold" data-testid="rpp-dirty-flag">● Unsaved changes</span>}
          <Button data-testid="rpp-reset" onClick={resetDefaults} variant="outline" className="bg-zinc-950 border-zinc-700 text-zinc-300">
            <RotateCcw className="h-4 w-4 mr-1.5"/>Reset
          </Button>
          <Button data-testid="rpp-save" onClick={save} disabled={!dirty || saving} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Save className="h-4 w-4 mr-1.5"/>Save Changes</>}
          </Button>
        </div>
      </div>

      {/* ── Top-level copy blocks ───────────────────────────────────────── */}
      <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3" data-testid="rpp-section-commitment">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Commitment / Compliance Block</div>
        <Field label="Commitment paragraph" testid="rpp-commitment">
          <Textarea
            data-testid="rpp-commitment-input"
            value={cfg.commitment_text}
            rows={3}
            onChange={(e) => update((c) => { c.commitment_text = e.target.value; })}
            className="bg-zinc-950 border-zinc-700 text-zinc-100"
          />
        </Field>
        <Field label="Rate-change paragraph (OSBI + safety messaging)" testid="rpp-rate-change">
          <Textarea
            data-testid="rpp-rate-change-input"
            value={cfg.rate_change_text}
            rows={4}
            onChange={(e) => update((c) => { c.rate_change_text = e.target.value; })}
            className="bg-zinc-950 border-zinc-700 text-zinc-100"
          />
        </Field>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3" data-testid="rpp-section-header">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Pricing Section Header</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Eyebrow (small label)" testid="rpp-section-header-label">
            <Input data-testid="rpp-section-header-input" value={cfg.section_header} onChange={(e) => update((c) => { c.section_header = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
          </Field>
          <Field label="Headline" testid="rpp-section-subheader">
            <Input data-testid="rpp-section-subheader-input" value={cfg.section_subheader} onChange={(e) => update((c) => { c.section_subheader = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
          </Field>
        </div>
        <Field label="Caption (small grey line under the headline)" testid="rpp-section-caption">
          <Input data-testid="rpp-section-caption-input" value={cfg.section_caption} onChange={(e) => update((c) => { c.section_caption = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
        </Field>
      </Card>

      {/* ── Panels editor ──────────────────────────────────────────────── */}
      <div className="space-y-4" data-testid="rpp-panels">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300">Pricing Panels</h3>
          <Button
            data-testid="rpp-panel-add"
            variant="outline"
            className="bg-zinc-950 border-amber-500/40 text-amber-300"
            onClick={() => update((c) => { c.panels.push({
              id: `panel-${Date.now()}`,
              title: "New Panel",
              subtitle: "",
              label: "Custom Rate",
              color: "indigo",
              rates: [{ k: "Base Rate", v: "$0.00", hi: true, _rid: `rate-${Date.now()}` }],
              estimate: "—",
              estimate_label: "Average Fare Range",
            }); })}
          >
            <Plus className="h-4 w-4 mr-1"/>Add Panel
          </Button>
        </div>

        {cfg.panels.map((p, pi) => (
          <Card key={p.id} className="bg-zinc-900 border-zinc-800 p-5 space-y-3" data-testid={`rpp-panel-${pi}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Panel #{pi + 1}</div>
              <div className="flex items-center gap-1">
                <Button data-testid={`rpp-panel-${pi}-up`}   variant="ghost" size="icon" disabled={pi === 0} onClick={() => update((c) => { [c.panels[pi - 1], c.panels[pi]] = [c.panels[pi], c.panels[pi - 1]]; })}><ArrowUp className="h-3.5 w-3.5"/></Button>
                <Button data-testid={`rpp-panel-${pi}-down`} variant="ghost" size="icon" disabled={pi === cfg.panels.length - 1} onClick={() => update((c) => { [c.panels[pi + 1], c.panels[pi]] = [c.panels[pi], c.panels[pi + 1]]; })}><ArrowDown className="h-3.5 w-3.5"/></Button>
                <Button data-testid={`rpp-panel-${pi}-del`}  variant="ghost" size="icon" className="text-red-400 hover:text-red-300" disabled={cfg.panels.length <= 1} onClick={() => { if (window.confirm(`Delete "${p.title}"?`)) update((c) => { c.panels.splice(pi, 1); }); }}><Trash2 className="h-3.5 w-3.5"/></Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Title" testid={`rpp-panel-${pi}-title`}>
                <Input value={p.title} onChange={(e) => update((c) => { c.panels[pi].title = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
              </Field>
              <Field label="Eyebrow label" testid={`rpp-panel-${pi}-label`}>
                <Input value={p.label} onChange={(e) => update((c) => { c.panels[pi].label = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
              </Field>
              <Field label="Color theme" testid={`rpp-panel-${pi}-color`}>
                <Select value={p.color} onValueChange={(v) => update((c) => { c.panels[pi].color = v; })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {COLOR_OPTIONS.map((co) => <SelectItem key={co.value} value={co.value}>{co.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Subtitle" testid={`rpp-panel-${pi}-subtitle`}>
              <Input value={p.subtitle} onChange={(e) => update((c) => { c.panels[pi].subtitle = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Estimate label (small text)" testid={`rpp-panel-${pi}-estimate-label`}>
                <Input value={p.estimate_label} onChange={(e) => update((c) => { c.panels[pi].estimate_label = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
              </Field>
              <Field label="Estimate value (hero text)" testid={`rpp-panel-${pi}-estimate`}>
                <Input value={p.estimate} onChange={(e) => update((c) => { c.panels[pi].estimate = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
              </Field>
            </div>

            {/* Rate rows */}
            <div className="space-y-1.5 pt-2 border-t border-zinc-800" data-testid={`rpp-panel-${pi}-rates`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Rate Rows</div>
                <Button
                  data-testid={`rpp-panel-${pi}-rate-add`}
                  variant="outline" size="sm"
                  className="bg-zinc-950 border-zinc-700"
                  onClick={() => update((c) => { c.panels[pi].rates.push({ k: "New row", v: "$0.00", hi: false, _rid: `rate-${Date.now()}-${Math.random().toString(36).slice(2,6)}` }); })}
                >
                  <Plus className="h-3 w-3 mr-1"/>Add Row
                </Button>
              </div>
              {p.rates.map((r, ri) => (
                <div key={r._rid} className="grid grid-cols-12 gap-2 items-center" data-testid={`rpp-panel-${pi}-rate-${ri}`}>
                  <Input
                    className="col-span-6 bg-zinc-950 border-zinc-700 text-zinc-100"
                    value={r.k}
                    placeholder="Label"
                    onChange={(e) => update((c) => { c.panels[pi].rates[ri].k = e.target.value; })}
                  />
                  <Input
                    className="col-span-4 bg-zinc-950 border-zinc-700 text-zinc-100 meter-display"
                    value={r.v}
                    placeholder="Value"
                    onChange={(e) => update((c) => { c.panels[pi].rates[ri].v = e.target.value; })}
                  />
                  <label className="col-span-1 flex items-center gap-1 text-[10px] text-zinc-400">
                    <Checkbox
                      checked={r.hi}
                      onCheckedChange={(v) => update((c) => { c.panels[pi].rates[ri].hi = !!v; })}
                    /> Hi
                  </label>
                  <Button
                    variant="ghost" size="icon" className="col-span-1 text-red-400 hover:text-red-300"
                    disabled={p.rates.length <= 1}
                    onClick={() => update((c) => { c.panels[pi].rates.splice(ri, 1); })}
                  >
                    <Trash2 className="h-3.5 w-3.5"/>
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* ── Long-distance notice ───────────────────────────────────────── */}
      <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3" data-testid="rpp-section-long-distance">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Long-Distance Travel Notice</div>
        <Field label="Title" testid="rpp-long-distance-title">
          <Input data-testid="rpp-long-distance-title-input" value={cfg.long_distance_title} onChange={(e) => update((c) => { c.long_distance_title = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
        </Field>
        <Field label="Body text" testid="rpp-long-distance-text">
          <Textarea data-testid="rpp-long-distance-text-input" rows={3} value={cfg.long_distance_text} onChange={(e) => update((c) => { c.long_distance_text = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
        </Field>
      </Card>

      {/* ── Payment info ────────────────────────────────────────────────── */}
      <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3" data-testid="rpp-section-payment">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Payment Information</div>
        <Field label="Intro line (above the payment method icons)" testid="rpp-payment-intro">
          <Input data-testid="rpp-payment-intro-input" value={cfg.payment_intro_text} onChange={(e) => update((c) => { c.payment_intro_text = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
        </Field>
        <div data-testid="rpp-payment-methods">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">Payment methods</Label>
          <div className="space-y-2">
            {/* v2.7.12 · payment_methods is a plain string[]; we use a
                content-derived stable key (value + position) so renames
                don't unmount the input. Add/remove forces remount, which
                is the expected behavior for that mutation. */}
            {cfg.payment_methods.map((m, mi) => (
              <div key={`pm-${mi}-${m.slice(0, 12)}`} className="flex items-center gap-2" data-testid={`rpp-payment-${mi}`}>
                <Input value={m} onChange={(e) => update((c) => { c.payment_methods[mi] = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
                <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300" disabled={cfg.payment_methods.length <= 1} onClick={() => update((c) => { c.payment_methods.splice(mi, 1); })}><Trash2 className="h-3.5 w-3.5"/></Button>
              </div>
            ))}
            <Button data-testid="rpp-payment-add" variant="outline" size="sm" className="bg-zinc-950 border-zinc-700" onClick={() => update((c) => { c.payment_methods.push("New method"); })}>
              <Plus className="h-3 w-3 mr-1"/>Add Method
            </Button>
          </div>
        </div>
        <Field label="Card processing fee notice" testid="rpp-cc-fee">
          <Textarea data-testid="rpp-cc-fee-input" rows={2} value={cfg.card_processing_fee_text} onChange={(e) => update((c) => { c.card_processing_fee_text = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
        </Field>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="rpp-section-footer">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Footer Disclaimer</div>
        <Textarea data-testid="rpp-footer-input" rows={2} value={cfg.footer_disclaimer} onChange={(e) => update((c) => { c.footer_disclaimer = e.target.value; })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
      </Card>

      <div className="flex items-center justify-between gap-3 flex-wrap text-[11px] text-zinc-500" data-testid="rpp-footer-controls">
        <div className="flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-amber-400"/>Changes affect the public Prices page immediately on next rider load.</div>
        <a href="/ride" target="_blank" rel="noreferrer" className="text-amber-300 hover:text-amber-200 inline-flex items-center gap-1" data-testid="rpp-preview-link">
          <ExternalLink className="h-3 w-3"/> Open rider portal in new tab
        </a>
      </div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={`field-${testid}`}>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
